import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"
import Dropzone from "dropzone"

// Disable Dropzone auto-discovery to prevent conflicts
Dropzone.autoDiscover = false

export default class extends Controller {
  static values = {
    url: String,
    uploadableId: String,
    uploadableType: String,
    canCreate: Boolean,
    canDelete: Boolean,
    baseUrl: String,
    maxFileSize: String
  }

  static targets = [
    "container", "dropzone", "dropzoneContainer", "filesList", "fileItem"
  ]

  connect() {
    this.uploads = []
    this.inheritedUploads = []
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

    // Create modal in body (like Vue Teleport)
    this.setupModal()

    // Show dropzone immediately if user can create (don't wait for files to load)
    if (this.canCreateValue) {
      this.containerTarget.classList.remove('hidden')
      if (this.hasDropzoneContainerTarget) {
        this.dropzoneContainerTarget.classList.remove('hidden')
      }
    }

    // Load initial files
    this.loadFiles()

    // Setup escape key handler for modal
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    // Force Dropzone auto-discovery to run again for this element
    this.ensureDropzoneInitialized()
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)

    // Clean up modal from body (use remove() which is safe even if element is not in DOM)
    if (this.bodyModalWrapper) {
      this.bodyModalWrapper.remove()
      this.bodyModalWrapper = null
      this.bodyModalTitle = null
      this.bodyModalDownload = null
      this.bodyModalIframe = null
    }
  }

  // Create modal in body (like Vue Teleport)
  setupModal() {
    // Create modal HTML and append to body
    const modalHTML = `
      <div class='manage_modal__wrapper hidden' id="file-manager-modal-${this.uploadableIdValue || 'default'}">
        <div class='manage_modal'>
          <header>
            <h1 id="file-manager-modal-title"></h1>
            <div class="button-group">
              <a id="file-manager-modal-download" href='' class='button button--icon button--unfilled'>
                <i class='far fa-download'></i>
              </a>
              <button type="button" id="file-manager-modal-close" class='button button--icon button--unfilled' aria-label="Close">
                <i class='far fa-times'></i>
              </button>
            </div>
          </header>
          <main>
            <iframe id="file-manager-modal-iframe" type="application/pdf" width="100%" height="750px"></iframe>
          </main>
        </div>
      </div>
    `

    document.body.insertAdjacentHTML('beforeend', modalHTML)

    // Store references to the modal elements
    this.modalId = `file-manager-modal-${this.uploadableIdValue || 'default'}`
    this.bodyModalWrapper = document.getElementById(this.modalId)
    this.bodyModalTitle = document.getElementById('file-manager-modal-title')
    this.bodyModalDownload = document.getElementById('file-manager-modal-download')
    this.bodyModalIframe = document.getElementById('file-manager-modal-iframe')

    // Add close handler
    document.getElementById('file-manager-modal-close').addEventListener('click', (e) => {
      e.preventDefault()
      this.closeModal()
    })
  }

  // Load files from server
  async loadFiles() {
    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()

      if (data.items !== undefined) {
        this.uploads = data.items
        this.inheritedUploads = data.inherited_items || []
      } else {
        this.uploads = data
        this.inheritedUploads = []
      }

      this.updateVisibility()
      this.updateFileCount()
      this.renderFilesList()
    } catch (error) {
      console.error('Error loading files:', error)
      // If we can create files, show the section anyway
      if (this.canCreateValue) {
        this.updateVisibility()
      }
    }
  }

  // Update visibility based on can_create or files existence
  updateVisibility() {
    const totalFiles = this.uploads.length + this.inheritedUploads.length
    const shouldShow = this.canCreateValue || totalFiles > 0

    this.containerTarget.classList.toggle('hidden', !shouldShow)

    if (shouldShow) {
      // Show dropzone if can create
      if (this.canCreateValue && this.hasDropzoneContainerTarget) {
        this.dropzoneContainerTarget.classList.remove('hidden')
      }

      // Show files list if there are files
      if (totalFiles > 0 && this.hasFilesListTarget) {
        this.filesListTarget.classList.remove('hidden')
      }
    }
  }

  // Render files list dynamically
  renderFilesList() {
    if (!this.hasFilesListTarget) return

    this.filesListTarget.replaceChildren()

    const totalFiles = this.uploads.length + this.inheritedUploads.length
    if (totalFiles === 0) return

    if (this.inheritedUploads.length > 0) {
      const label = document.createElement('div')
      label.className = 'relation-inherited-label'
      label.textContent = 'Inherited from process'
      this.filesListTarget.appendChild(label)

      this.inheritedUploads.forEach((upload, index) => {
        this.filesListTarget.appendChild(this.buildFileItem(upload, index, true))
      })

      if (this.uploads.length > 0) {
        const divider = document.createElement('hr')
        divider.className = 'relation-section-divider'
        this.filesListTarget.appendChild(divider)
      }
    }

    this.uploads.forEach((upload, index) => {
      this.filesListTarget.appendChild(this.buildFileItem(upload, index, false))
    })
  }

  buildFileItem(upload, index, inherited = false) {
    const section = document.createElement('section')
    section.dataset.index = index

    if (inherited) {
      section.appendChild(this.buildFileDisplay(upload, index, true))
      return section
    }

    section.setAttribute('data-file-manager-target', 'fileItem')
    section.dataset.slug = upload.slug || ''

    section.appendChild(this.buildEditForm(upload))
    section.appendChild(this.buildFileDisplay(upload, index, false))

    const buttonGroup = this.buildButtonGroup(upload, index)
    if (buttonGroup) section.appendChild(buttonGroup)

    return section
  }

  buildEditForm(upload) {
    const form = document.createElement('form')
    form.setAttribute('data-file-manager-target', 'editForm')
    form.setAttribute('data-action', 'submit->file-manager#saveEdit')
    form.className = 'asset-sidebar__form hidden'

    const input = document.createElement('input')
    input.type = 'text'
    input.setAttribute('data-file-manager-target', 'editInput')
    input.placeholder = 'New name...'
    input.value = upload.name || ''
    form.appendChild(input)

    const buttonGroup = document.createElement('div')
    buttonGroup.className = 'button-group'

    const saveButton = document.createElement('button')
    saveButton.type = 'submit'
    saveButton.className = 'button button--small button--primary'
    saveButton.textContent = 'Save'
    buttonGroup.appendChild(saveButton)

    const cancelButton = document.createElement('button')
    cancelButton.type = 'button'
    cancelButton.setAttribute('data-action', 'click->file-manager#cancelEdit')
    cancelButton.className = 'button button--small button--secondary'
    cancelButton.textContent = 'Cancel'
    buttonGroup.appendChild(cancelButton)

    form.appendChild(buttonGroup)
    return form
  }

  buildFileDisplay(upload, index, inherited = false) {
    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-file-manager-target', 'fileDisplay')

    const heading = document.createElement('h3')

    const filetype = document.createElement('small')
    filetype.className = 'asset-sidebar__filetype'
    filetype.textContent = upload.ext || '?'
    heading.appendChild(filetype)

    const title = `${upload.size} - Uploaded ${upload.created_date}`

    let link
    if (upload.ext === 'pdf' && !inherited) {
      link = document.createElement('button')
      link.type = 'button'
      link.className = 'button--link'
      link.setAttribute('data-action', 'click->file-manager#openPdfModal')
      link.dataset.index = index
    } else {
      link = document.createElement('a')
      link.setAttribute('href', upload.url || '')
      if (inherited) {
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
      }
    }
    link.title = title
    link.textContent = upload.name || ''
    heading.appendChild(link)

    wrapper.appendChild(heading)
    return wrapper
  }

  buildButtonGroup(upload, index) {
    if (!this.canCreateValue || !upload.slug) return null

    const group = document.createElement('div')
    group.className = 'button-group button-group--condensed'

    const editButton = document.createElement('button')
    editButton.type = 'button'
    editButton.setAttribute('data-action', 'click->file-manager#startEdit')
    editButton.dataset.index = index
    editButton.dataset.name = upload.name || ''
    editButton.className = 'button button--icon button--unfilled button--icon--small'
    editButton.setAttribute('aria-label', 'Edit file')
    editButton.appendChild(this.buildIcon('fas fa-edit'))
    group.appendChild(editButton)

    if (this.canDeleteValue) {
      const deleteButton = document.createElement('button')
      deleteButton.type = 'button'
      deleteButton.setAttribute('data-action', 'click->file-manager#deleteFile')
      deleteButton.dataset.slug = upload.slug
      deleteButton.dataset.index = index
      deleteButton.className = 'button button--icon button--unfilled button--icon--small button--icon--danger'
      deleteButton.setAttribute('aria-label', 'Delete file')
      deleteButton.appendChild(this.buildIcon('fas fa-trash-can'))
      group.appendChild(deleteButton)
    }

    return group
  }

  buildIcon(className) {
    const icon = document.createElement('i')
    icon.className = className
    return icon
  }

  // Ensure Dropzone is initialized for Turbo navigation
  ensureDropzoneInitialized() {
    if (!this.canCreateValue || !this.hasDropzoneTarget) return

    // If already initialized, just setup events
    if (this.dropzoneTarget.dropzone) {
      this.setupDropzoneEvents()
      return
    }

    // Manually initialize since auto-discovery is disabled
    if (this.dropzoneTarget.classList.contains('dropzone')) {
      new Dropzone(this.dropzoneTarget)

      // Setup events after initialization
      setTimeout(() => this.setupDropzoneEvents(), 50)
    }
  }

  // Setup event handlers for auto-discovered Dropzone
  setupDropzoneEvents() {
    if (!this.canCreateValue || !this.hasDropzoneTarget) return

    // Find the auto-discovered Dropzone instance
    const dropzoneElement = this.dropzoneTarget
    if (dropzoneElement.dropzone) {
      const dropzone = dropzoneElement.dropzone

      // Configure dropzone options (same as vue2-dropzone)
      dropzone.options.timeout = 90000
      dropzone.options.parallelUploads = 10
      dropzone.options.thumbnailWidth = 150
      dropzone.options.maxFilesize = parseInt(this.maxFileSizeValue)

      // Add CSRF token and params
      dropzone.options.headers = {
        'X-CSRF-Token': this.csrfToken
      }
      dropzone.options.params = {
        uploadable_id: this.uploadableIdValue,
        uploadable_type: this.uploadableTypeValue
      }

      // Setup event handlers (same as vue2-dropzone)
      dropzone.on('success', (file, response) => {
        this.loadFiles()
        flash('File added', 'success')
      })

      dropzone.on('error', (file, errorMessage) => {
        flash('Upload failed', 'error')
      })
    }
  }

  // Start editing a file name
  startEdit(event) {
    event.preventDefault()
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    const name = event.currentTarget.dataset.name

    // Close any other open edit forms first
    this.clearAllEdits()

    if (fileItem) {
      const editForm = fileItem.querySelector('[data-file-manager-target="editForm"]')
      const fileDisplay = fileItem.querySelector('[data-file-manager-target="fileDisplay"]')
      const buttonGroup = fileItem.querySelector('.button-group.button-group--condensed')
      const editInput = fileItem.querySelector('[data-file-manager-target="editInput"]')

      fileItem.dataset.editing = 'true'
      fileDisplay.classList.add('hidden')
      editForm.classList.remove('hidden')
      if (buttonGroup) buttonGroup.classList.add('hidden')

      if (editInput) {
        editInput.value = name
        editInput.focus()
      }
    }
  }

  // Cancel editing
  cancelEdit(event) {
    event.preventDefault()
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    this.clearEdit(fileItem)
  }

  clearEdit(fileItem) {
    if (!fileItem || fileItem.dataset.editing !== 'true') return

    const editForm = fileItem.querySelector('[data-file-manager-target="editForm"]')
    const fileDisplay = fileItem.querySelector('[data-file-manager-target="fileDisplay"]')
    const buttonGroup = fileItem.querySelector('.button-group.button-group--condensed')

    editForm.classList.add('hidden')
    fileDisplay.classList.remove('hidden')
    if (buttonGroup) buttonGroup.classList.remove('hidden')

    delete fileItem.dataset.editing
  }

  clearAllEdits() {
    this.fileItemTargets
      .filter(item => item.dataset.editing === 'true')
      .forEach(item => this.clearEdit(item))
  }

  // Save edited name
  async saveEdit(event) {
    event.preventDefault()

    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    if (!fileItem || fileItem.dataset.editing !== 'true') return

    const editInput = fileItem.querySelector('[data-file-manager-target="editInput"]')
    const slug = fileItem.dataset.slug
    const newName = editInput.value.trim()

    if (!newName) return

    try {
      const response = await fetch(`${this.baseUrlValue}/uploads/${slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ name: newName })
      })

      if (response.ok) {
        await this.loadFiles()
        flash('File updated', 'success')
        this.clearEdit(fileItem)
      } else {
        throw new Error('Update failed')
      }
    } catch (error) {
      console.error('Update error:', error)
      flash('Update failed', 'error')
    }
  }

  // Delete a file
  async deleteFile(event) {
    event.preventDefault()

    if (!confirm('Are you sure?')) return

    const slug = event.currentTarget.dataset.slug

    try {
      const response = await fetch(`${this.baseUrlValue}/uploads/${slug}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (response.ok) {
        await this.loadFiles()
      } else {
        throw new Error('Delete failed')
      }
    } catch (error) {
      console.error('Delete error:', error)
      flash('Delete failed', 'error')
    }
  }

  // Open PDF modal
  openPdfModal(event) {
    event.preventDefault()
    const fileItem = event.currentTarget.closest('[data-file-manager-target="fileItem"]')
    const index = parseInt(fileItem.dataset.index)

    if (this.uploads[index]) {
      const upload = this.uploads[index]

      if (this.bodyModalTitle) this.bodyModalTitle.textContent = upload.name
      if (this.bodyModalDownload) this.bodyModalDownload.href = upload.url
      if (this.bodyModalIframe) this.bodyModalIframe.src = `${upload.url}?inline=true`
      if (this.bodyModalWrapper) {
        this.bodyModalWrapper.classList.remove('hidden')
        this.bodyModalWrapper.dataset.currentFile = index
      }
    }
  }

  // Close modal
  closeModal(event) {
    if (event) event.preventDefault()
    if (this.bodyModalWrapper) {
      this.bodyModalWrapper.classList.add('hidden')
      delete this.bodyModalWrapper.dataset.currentFile
    }
    if (this.bodyModalIframe) {
      this.bodyModalIframe.src = ''
    }
  }

  // Handle keyboard events
  handleKeydown(event) {
    if (event.key === 'Escape' && this.bodyModalWrapper && this.bodyModalWrapper.dataset.currentFile) {
      this.closeModal()
    }
  }

  // Update file count display
  updateFileCount() {
    const totalFiles = this.uploads.length + this.inheritedUploads.length

    // Find the Files widget toggler controller and update its size
    const allWidgetTogglers = document.querySelectorAll('[data-controller*="widget-toggler"]')

    const filesWidgetToggler = Array.from(allWidgetTogglers).find(element =>
      element.dataset.name === 'Files'
    )

    if (filesWidgetToggler) {
      filesWidgetToggler.dataset.size = totalFiles.toString()

      const stimulusController = this.application.getControllerForElementAndIdentifier(filesWidgetToggler, 'widget-toggler')

      if (stimulusController) {
        stimulusController.handleSizeUpdate({
          detail: { size: totalFiles }
        })
      }
    } else {
      // Fallback: manually update the text
      const textSpan = this.element.querySelector('.asset-sidebar__group--toggle-text')
      if (textSpan) {
        textSpan.textContent = `Files (${totalFiles})`
      }
    }
  }
}
